import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, Inbox, Loader2, Mail, RefreshCw, X } from "lucide-react";

/* ----------------------- GUIDE Referral Sheets view -----------------------
 * Admin-only. Pick any caregiver session and assemble a referral packet
 * for a GUIDE provider: continuity passport + all screener scores + flags
 * + uploads, with copy-to-clipboard + open-in-email actions.
 */
/* ----------------------- Partner Leads view -----------------------
 * Inbox for /api/partners/interest submissions (landing page + Resources
 * page "Become a partner" form). Each lead shows org/email/role/notes/
 * status. The big-deal action is "Approve into directory" — converts a
 * lead into a Community Partner record (status: coming_soon) so it
 * immediately shows up at /resources/community-partners with no manual
 * retyping. Lead's status flips to pilot_started for tracking.
 */
const LEAD_STATUSES = [
    { v: "new", label: "New", tone: "bg-coral-light text-coral-dark border-coral-softer" },
    { v: "contacted", label: "Contacted", tone: "bg-yellow-light text-yellow-dark border-yellow" },
    { v: "pilot_started", label: "In directory", tone: "bg-sage-light text-sage-dark border-sage-soft" },
    { v: "dropped", label: "Dropped", tone: "bg-cream-dark text-[#4C5551] border-cream-dark" },
];

const LEAD_CATEGORY_GUESS = [
    { match: /(spa|massage|wellness|sleep|sauna|facial)/i,        category: "spa_self_care" },
    { match: /(gym|fitness|yoga|pilates|movement|barre)/i,        category: "fitness" },
    { match: /(therap|counsel|psycholog|psychiatr|grief|mental)/i, category: "mental_wellness" },
    { match: /(telehealth|virtual|video doctor|telemedicine|online doctor)/i, category: "virtual_doctor" },
    { match: /(meal|food|grocery|nutrition|delivery|chef)/i,      category: "meals" },
    { match: /(respite|home aide|adult day|companion)/i,          category: "respite" },
    { match: /(support|group|community|circle)/i,                  category: "support_group" },
    { match: /(faith|church|temple|mosque|spiritual)/i,            category: "faith" },
];

function guessCategoryFromLead(lead) {
    const blob = `${lead.org_name || ""} ${lead.notes || ""} ${lead.role || ""}`;
    for (const g of LEAD_CATEGORY_GUESS) {
        if (g.match.test(blob)) return g.category;
    }
    return "other";
}

export default function PartnerLeadsView() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("new");
    const [approving, setApproving] = useState(null); // lead being approved (inline editor)

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/partners/interest");
            setLeads(r.data || []);
        } catch (e) {
            toast.error("Couldn't load partner leads.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const updateStatus = async (lead, status, note = "") => {
        try {
            await api.patch(`/partners/interest/${lead.id}`, { status, note });
            toast.success(`Marked ${status.replace("_", " ")}.`);
            await load();
        } catch (e) {
            toast.error("Couldn't update status.");
        }
    };

    const approveIntoDirectory = async (lead, partnerDraft) => {
        try {
            await api.post("/admin/community-partners", partnerDraft);
            await api.patch(`/partners/interest/${lead.id}`, {
                status: "pilot_started",
                note: `Approved into community directory as "${partnerDraft.name}".`,
            });
            toast.success("Added to community partners.");
            setApproving(null);
            await load();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Couldn't add partner.");
        }
    };

    const visible = leads.filter(l => filter === "all" || (l.status || "new") === filter);
    const counts = LEAD_STATUSES.reduce((acc, s) => {
        acc[s.v] = leads.filter(l => (l.status || "new") === s.v).length;
        return acc;
    }, {});

    return (
        <section data-testid="admin-partner-leads">
            <header className="flex items-start gap-4 justify-between flex-wrap mb-6">
                <div>
                    <h1 className="font-heading text-2xl md:text-3xl text-[#12776F]">Partner leads</h1>
                    <p className="text-sm text-[#4C5551] mt-1 max-w-2xl">
                        Spas, gyms, therapists, meal services, virtual doctors and respite teams who applied via the
                        landing page or the &quot;Become a partner&quot; form on <Link to="/help-finder#community-partners" className="text-teal-dark underline">/help-finder</Link>.
                        Click <strong>Approve &amp; add to directory</strong> to drop a lead straight into <Link to="/resources/community-partners" className="text-teal-dark underline">/resources/community-partners</Link> as <em>Coming soon</em>.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={load}
                    data-testid="leads-refresh"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-medium bg-white border border-cream-dark text-[#1F2624] hover:bg-cream-dark/30"
                >
                    <RefreshCw size={14} strokeWidth={1.8} /> Refresh
                </button>
            </header>

            <div className="flex flex-wrap gap-2 mb-4" data-testid="leads-tabs">
                {[{ v: "all", label: "All", tone: "bg-cream-dark text-[#1F2624] border-cream-dark" }, ...LEAD_STATUSES].map(s => (
                    <button
                        key={s.v}
                        type="button"
                        onClick={() => setFilter(s.v)}
                        data-testid={`leads-tab-${s.v}`}
                        className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-2xl text-xs font-medium border transition-colors ${
                            filter === s.v ? "bg-teal text-white border-teal" : "bg-white text-[#4C5551] border-cream-dark hover:bg-cream-dark/30"
                        }`}
                    >
                        {s.label}
                        <span className="text-[10px] opacity-80">{s.v === "all" ? leads.length : (counts[s.v] || 0)}</span>
                    </button>
                ))}
            </div>

            {loading ? (
                <p className="text-sm text-[#6E7A74] inline-flex items-center gap-2" data-testid="leads-loading">
                    <Loader2 size={14} className="animate-spin" /> Loading leads…
                </p>
            ) : visible.length === 0 ? (
                <div className="ml-card p-6 text-center text-sm text-[#4C5551]" data-testid="leads-empty">
                    No leads in &quot;{filter}&quot; yet. New applications land here automatically.
                </div>
            ) : (
                <ul className="space-y-3" data-testid="leads-list">
                    {visible.map(l => {
                        const tone = (LEAD_STATUSES.find(s => s.v === (l.status || "new")) || LEAD_STATUSES[0]).tone;
                        const isApproving = approving?.id === l.id;
                        return (
                            <li key={l.id} className="ml-card p-4 md:p-5 bg-white" data-testid={`lead-${l.id}`}>
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                            <span className={`inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-2xl border ${tone}`}>
                                                {(LEAD_STATUSES.find(s => s.v === (l.status || "new")) || LEAD_STATUSES[0]).label}
                                            </span>
                                            <span className="text-xs text-[#6E7A74]">{l.created_at?.slice(0, 16).replace("T", " ")}</span>
                                        </div>
                                        <p className="font-heading text-lg text-[#1F2624]">{l.org_name}</p>
                                        <p className="text-sm text-[#4C5551] mt-0.5">
                                            <a href={`mailto:${l.email}`} className="text-teal-dark underline">{l.email}</a>
                                            {l.role ? <span> · {l.role}</span> : null}
                                        </p>
                                        {l.notes && (
                                            <p className="text-sm text-[#1F2624] mt-2 bg-cream-light rounded-2xl p-3 border border-cream-dark whitespace-pre-wrap">
                                                {l.notes}
                                            </p>
                                        )}
                                        {l.referer && (
                                            <p className="text-[11px] text-[#6E7A74] mt-2 truncate">via {l.referer}</p>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap gap-2 shrink-0">
                                        {(l.status || "new") !== "pilot_started" && (
                                            <button
                                                type="button"
                                                onClick={() => setApproving({
                                                    ...l,
                                                    _draft: {
                                                        name: l.org_name || "",
                                                        category: guessCategoryFromLead(l),
                                                        status: "coming_soon",
                                                        country: "US",
                                                        region: "",
                                                        description: (l.notes || "").slice(0, 600),
                                                        url: "",
                                                        perk: "",
                                                    },
                                                })}
                                                data-testid={`lead-approve-${l.id}`}
                                                className="ml-btn-primary !min-h-[36px] !px-3.5 !py-1.5 !text-xs inline-flex items-center gap-1.5"
                                            >
                                                <Check size={13} strokeWidth={2} /> Approve &amp; add to directory
                                            </button>
                                        )}
                                        {(l.status || "new") === "new" && (
                                            <button
                                                type="button"
                                                onClick={() => updateStatus(l, "contacted")}
                                                data-testid={`lead-contacted-${l.id}`}
                                                className="!min-h-[36px] !px-3.5 !py-1.5 !text-xs inline-flex items-center gap-1.5 rounded-2xl font-medium bg-white border border-cream-dark text-[#1F2624] hover:border-teal-dark hover:text-teal-dark transition-colors"
                                            >
                                                <Mail size={13} strokeWidth={1.8} /> Mark contacted
                                            </button>
                                        )}
                                        {(l.status || "new") !== "dropped" && (l.status || "new") !== "pilot_started" && (
                                            <button
                                                type="button"
                                                onClick={() => updateStatus(l, "dropped")}
                                                data-testid={`lead-drop-${l.id}`}
                                                className="!min-h-[36px] !px-3.5 !py-1.5 !text-xs inline-flex items-center gap-1.5 rounded-2xl font-medium bg-white border border-cream-dark text-[#6E7A74] hover:text-coral-dark hover:border-coral-softer transition-colors"
                                            >
                                                <X size={13} strokeWidth={1.8} /> Drop
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {isApproving && (
                                    <PartnerApprovalEditor
                                        draft={approving._draft}
                                        onCancel={() => setApproving(null)}
                                        onConfirm={(partner) => approveIntoDirectory(l, partner)}
                                    />
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
}

/* Inline editor that appears under a lead row when "Approve" is clicked.
   Prefills from the lead, lets the admin tweak category/status/region/url/
   perk in one place, then posts to /admin/community-partners and flips
   the lead's status to pilot_started. */
const APPROVAL_CATEGORIES = [
    { v: "mental_wellness", label: "Mental wellness" },
    { v: "spa_self_care", label: "Spa & self-care" },
    { v: "fitness", label: "Gyms & fitness" },
    { v: "virtual_doctor", label: "Virtual doctors" },
    { v: "meals", label: "Meal services" },
    { v: "respite", label: "Respite care" },
    { v: "support_group", label: "Support groups" },
    { v: "faith", label: "Faith communities" },
    { v: "other", label: "Other" },
];

function PartnerApprovalEditor({ draft, onCancel, onConfirm }) {
    const [form, setForm] = useState(draft);
    const [saving, setSaving] = useState(false);
    const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const submit = async (e) => {
        e.preventDefault();
        if (!form.name?.trim()) {
            toast.error("Name is required.");
            return;
        }
        setSaving(true);
        await onConfirm(form);
        setSaving(false);
    };
    return (
        <form
            onSubmit={submit}
            data-testid="lead-approve-editor"
            className="mt-4 pt-4 border-t border-cream-dark grid grid-cols-1 md:grid-cols-2 gap-3"
        >
            <label className="block md:col-span-2">
                <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">Public name</span>
                <input type="text" value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="lead-approve-name" className="ml-input w-full" required />
            </label>
            <label className="block">
                <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">Category</span>
                <select value={form.category} onChange={(e) => update("category", e.target.value)} data-testid="lead-approve-category" className="ml-input w-full">
                    {APPROVAL_CATEGORIES.map(c => <option key={c.v} value={c.v}>{c.label}</option>)}
                </select>
            </label>
            <label className="block">
                <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">Status</span>
                <select value={form.status} onChange={(e) => update("status", e.target.value)} data-testid="lead-approve-status" className="ml-input w-full">
                    <option value="coming_soon">Coming soon (default)</option>
                    <option value="live">Live</option>
                    <option value="draft">Draft (hidden)</option>
                </select>
            </label>
            <label className="block">
                <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">Country</span>
                <input type="text" value={form.country} onChange={(e) => update("country", e.target.value.toUpperCase())} data-testid="lead-approve-country" maxLength={6} className="ml-input w-full" placeholder="US, CA, GLOBAL" />
            </label>
            <label className="block">
                <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">Region (optional)</span>
                <input type="text" value={form.region || ""} onChange={(e) => update("region", e.target.value)} data-testid="lead-approve-region" className="ml-input w-full" placeholder="e.g. Bay Area, TX" />
            </label>
            <label className="block md:col-span-2">
                <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">Website (optional)</span>
                <input type="text" value={form.url || ""} onChange={(e) => update("url", e.target.value)} data-testid="lead-approve-url" className="ml-input w-full" placeholder="https://…" />
            </label>
            <label className="block md:col-span-2">
                <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">Caregiver perk (optional)</span>
                <input type="text" value={form.perk || ""} onChange={(e) => update("perk", e.target.value)} data-testid="lead-approve-perk" className="ml-input w-full" placeholder="e.g. 20% off for caregivers" />
            </label>
            <label className="block md:col-span-2">
                <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">Description shown to caregivers</span>
                <textarea rows={3} value={form.description || ""} onChange={(e) => update("description", e.target.value)} data-testid="lead-approve-description" className="ml-input w-full" />
            </label>
            <div className="md:col-span-2 flex items-center gap-2 pt-1">
                <button type="submit" disabled={saving} data-testid="lead-approve-confirm" className="ml-btn-primary !min-h-[40px] !px-4 !py-2 !text-sm disabled:opacity-60 inline-flex items-center gap-1.5">
                    <Check size={14} strokeWidth={2} /> {saving ? "Adding…" : "Confirm — add to directory"}
                </button>
                <button type="button" onClick={onCancel} data-testid="lead-approve-cancel" className="!min-h-[40px] !px-4 !py-2 !text-sm inline-flex items-center gap-1.5 rounded-2xl font-medium bg-white border border-cream-dark text-[#1F2624] hover:bg-cream-dark/30">
                    Cancel
                </button>
            </div>
        </form>
    );
}


